import { createSvgEl } from './svg';
import { createDragPoint, type DragPointHandle, type DragSpec } from './drag-point';
import type { Vec2 } from '@learnforge/demo-math/geometry';
import { add, dist } from '@learnforge/demo-math/geometry';

export interface PointStyle {
  className?: string;
  radius?: number;
}

export interface StrokeStyle {
  className?: string;
  dashed?: boolean;
}

export interface FillStyle {
  className?: string;
}

export type FigureElement =
  | { kind: 'point'; id: string; at: Vec2; draggable?: DragSpec; style?: PointStyle }
  | { kind: 'segment'; id: string; from: string; to: string; style?: StrokeStyle }
  | { kind: 'line' | 'ray'; id: string; through: [string, string]; style?: StrokeStyle }
  | { kind: 'polygon'; id: string; vertices: string[]; style?: FillStyle }
  | { kind: 'circle'; id: string; center: string; through: string; style?: StrokeStyle }
  | { kind: 'arc'; id: string; center: string; from: string; to: string; style?: StrokeStyle }
  | {
      kind: 'label';
      id: string;
      anchor: string;
      offset: Vec2;
      text: string;
      /**
       * Horizontal text alignment. Defaults to 'start' (the SVG default) so
       * existing labels are unaffected. Set 'middle' to centre a label on its
       * offset position — used to seat labels cleanly outside a circle.
       */
      textAnchor?: 'start' | 'middle' | 'end';
    };

export interface FigureOptions {
  viewBox: { width: number; height: number };
}

interface NodeEntry {
  element: SVGElement;
  dragHandle?: DragPointHandle;
  kind: string;
}

export interface FigureHandle {
  update(elements: readonly FigureElement[]): void;
  onDrag(cb: (pointId: string, at: Vec2) => void): void;
  destroy(): void;
}

const DEFAULT_POINT_RADIUS = 5;
const DASH_ARRAY = '5,5';

/**
 * Clips a line (infinite in both directions) to the viewBox rectangle using
 * parametric line representation. Returns the two clipped endpoints.
 */
function clipLineToRect(
  p1: Vec2,
  p2: Vec2,
  rect: { x: number; y: number; width: number; height: number },
): [Vec2, Vec2] {
  const dx = p2.x - p1.x;
  const dy = p2.y - p1.y;

  const xMin = rect.x;
  const xMax = rect.x + rect.width;
  const yMin = rect.y;
  const yMax = rect.y + rect.height;

  let tMin = -Infinity;
  let tMax = Infinity;

  // Parametric line: P(t) = p1 + t * (p2 - p1)
  // Clip against each edge

  // X clipping
  if (Math.abs(dx) > 1e-6) {
    const t1 = (xMin - p1.x) / dx;
    const t2 = (xMax - p1.x) / dx;
    const tLeft = Math.min(t1, t2);
    const tRight = Math.max(t1, t2);
    tMin = Math.max(tMin, tLeft);
    tMax = Math.min(tMax, tRight);
  } else if (p1.x < xMin || p1.x > xMax) {
    return [p1, p1]; // Line doesn't intersect
  }

  // Y clipping
  if (Math.abs(dy) > 1e-6) {
    const t1 = (yMin - p1.y) / dy;
    const t2 = (yMax - p1.y) / dy;
    const tTop = Math.min(t1, t2);
    const tBottom = Math.max(t1, t2);
    tMin = Math.max(tMin, tTop);
    tMax = Math.min(tMax, tBottom);
  } else if (p1.y < yMin || p1.y > yMax) {
    return [p1, p1]; // Line doesn't intersect
  }

  if (tMin > tMax) {
    return [p1, p1]; // No intersection
  }

  const end1 = {
    x: p1.x + tMin * dx,
    y: p1.y + tMin * dy,
  };

  const end2 = {
    x: p1.x + tMax * dx,
    y: p1.y + tMax * dy,
  };

  return [end1, end2];
}

/**
 * Clips a ray (from p1 through p2) to the viewBox rectangle.
 * Returns the start point and the clipped end point.
 */
function clipRayToRect(
  p1: Vec2,
  p2: Vec2,
  rect: { x: number; y: number; width: number; height: number },
): [Vec2, Vec2] {
  const dx = p2.x - p1.x;
  const dy = p2.y - p1.y;

  const xMin = rect.x;
  const xMax = rect.x + rect.width;
  const yMin = rect.y;
  const yMax = rect.y + rect.height;

  let tMax = Infinity;

  // Parametric ray: P(t) = p1 + t * (p2 - p1), where t >= 0

  // X slab clipping
  if (Math.abs(dx) > 1e-6) {
    const t1 = (xMin - p1.x) / dx;
    const t2 = (xMax - p1.x) / dx;
    const tRight = dx > 0 ? t2 : t1;
    tMax = Math.min(tMax, tRight);
  } else if (p1.x < xMin || p1.x > xMax) {
    // Ray starts outside X bounds (line is vertical)
    tMax = 0;
  }

  // Y slab clipping
  if (Math.abs(dy) > 1e-6) {
    const t1 = (yMin - p1.y) / dy;
    const t2 = (yMax - p1.y) / dy;
    const tBottom = dy > 0 ? t2 : t1;
    tMax = Math.min(tMax, tBottom);
  } else if (p1.y < yMin || p1.y > yMax) {
    // Ray starts outside Y bounds (line is horizontal)
    tMax = 0;
  }

  if (tMax < 0) {
    return [p1, p1];
  }

  const end = {
    x: p1.x + tMax * dx,
    y: p1.y + tMax * dy,
  };

  return [p1, end];
}

/**
 * Compute arc path data. Returns the SVG path 'd' attribute for an elliptic arc.
 * Arc goes from `from` point to `to` point around `center` with radius = dist(center, from).
 */
function getArcPath(center: Vec2, from: Vec2, to: Vec2): string {
  const r = dist(center, from);

  // Compute angle difference
  const angleFrom = Math.atan2(from.y - center.y, from.x - center.x);
  const angleTo = Math.atan2(to.y - center.y, to.x - center.x);

  // Determine if the arc is large (> 180 degrees)
  let dangle = angleTo - angleFrom;
  if (dangle < 0) {
    dangle += 2 * Math.PI;
  }
  const largeArc = dangle > Math.PI ? 1 : 0;

  return `M ${from.x} ${from.y} A ${r} ${r} 0 ${largeArc} 1 ${to.x} ${to.y}`;
}

export function createFigure(
  parent: SVGSVGElement | SVGGElement,
  options: FigureOptions,
): FigureHandle {
  const { viewBox } = options;
  const rect = { x: 0, y: 0, width: viewBox.width, height: viewBox.height };

  // Z-order layer groups (rendered back to front)
  const polygonLayer = createSvgEl('g', { 'data-layer': 'polygons' });
  const circleArcLayer = createSvgEl('g', { 'data-layer': 'circles-arcs' });
  const lineSegmentLayer = createSvgEl('g', { 'data-layer': 'lines-segments' });
  const pointLayer = createSvgEl('g', { 'data-layer': 'points' });
  const labelLayer = createSvgEl('g', { 'data-layer': 'labels' });

  parent.appendChild(polygonLayer);
  parent.appendChild(circleArcLayer);
  parent.appendChild(lineSegmentLayer);
  parent.appendChild(pointLayer);
  parent.appendChild(labelLayer);

  // Keyed node storage
  const nodeMap = new Map<string, NodeEntry>();

  // Drag callbacks (accumulative, not clobbering)
  const dragCallbacks: Array<(pointId: string, at: Vec2) => void> = [];

  /**
   * Map a raw pointer position (clientX/clientY) into the figure's world
   * coordinates using the live screen CTM, so a dragged point tracks the cursor
   * 1:1 regardless of how the viewBox is scaled to the rendered pixel size.
   * Falls back to identity when no CTM is available (e.g. jsdom in tests).
   */
  function screenToWorld(screen: Vec2): Vec2 {
    const svg = parent.ownerSVGElement ?? (parent as SVGSVGElement);
    const ctm = svg.getScreenCTM?.();
    if (!ctm || typeof svg.createSVGPoint !== 'function') {
      return screen;
    }
    const pt = svg.createSVGPoint();
    pt.x = screen.x;
    pt.y = screen.y;
    const local = pt.matrixTransform(ctm.inverse());
    return { x: local.x, y: local.y };
  }

  /**
   * Resolve a point id to its current Vec2 position.
   * Returns null if point not found.
   */
  function resolvePoint(id: string, pointMap: Map<string, Vec2>): Vec2 | null {
    return pointMap.get(id) ?? null;
  }

  /**
   * Get or create a node with the given id and kind.
   * For existing nodes with the same kind, reuses the element.
   * If kind has changed, tears down the old node and creates a new one.
   */
  function getOrCreateNode(id: string, kind: string, layer: SVGGElement): SVGElement {
    const existing = nodeMap.get(id);

    // If entry exists but kind has changed, tear it down
    if (existing && existing.kind !== kind) {
      if (existing.dragHandle) {
        existing.dragHandle.destroy();
      }
      const el = existing.element;
      if (el.parentNode) {
        el.parentNode.removeChild(el);
      }
      nodeMap.delete(id);
    } else if (existing && existing.kind === kind) {
      return existing.element;
    }

    let element: SVGElement;

    switch (kind) {
      case 'point':
        element = createSvgEl('circle');
        break;
      case 'segment':
      case 'line':
      case 'ray':
        element = createSvgEl('line');
        break;
      case 'polygon':
        element = createSvgEl('polygon');
        break;
      case 'circle':
        element = createSvgEl('circle');
        break;
      case 'arc':
        element = createSvgEl('path');
        break;
      case 'label':
        element = createSvgEl('text');
        break;
      default:
        element = createSvgEl('g');
    }

    element.setAttribute('data-figure-id', id);
    layer.appendChild(element);

    nodeMap.set(id, { element, kind });

    return element;
  }

  /**
   * Update or remove nodes based on the new element list.
   */
  function updateScene(elements: readonly FigureElement[]): void {
    // Build a map of point ids to positions
    const pointMap = new Map<string, Vec2>();
    for (const elem of elements) {
      if (elem.kind === 'point') {
        pointMap.set(elem.id, elem.at);
      }
    }

    // Track which ids are still present
    const presentIds = new Set<string>();
    for (const elem of elements) {
      presentIds.add(elem.id);
    }

    // Remove nodes that are no longer present
    for (const [id, entry] of Array.from(nodeMap.entries())) {
      if (!presentIds.has(id)) {
        if (entry.dragHandle) {
          entry.dragHandle.destroy();
        }
        const el = entry.element;
        if (el.parentNode) {
          el.parentNode.removeChild(el);
        }
        nodeMap.delete(id);
      }
    }

    // Update or create nodes for each element
    for (const elem of elements) {
      switch (elem.kind) {
        case 'point': {
          const radius = elem.style?.radius ?? DEFAULT_POINT_RADIUS;
          const entry = nodeMap.get(elem.id);

          /**
           * Handle drag vs static points.
           * Note: style.radius and style.className are applied only at drag-handle
           * creation; they are not re-applied on subsequent updates (known limitation).
           */
          if (elem.draggable) {
            // Create or update drag point
            if (!entry?.dragHandle) {
              // Create new drag point
              const dragHandle = createDragPoint({
                parent: pointLayer,
                at: elem.at,
                spec: elem.draggable,
                onMove: (at: Vec2) => {
                  for (const cb of dragCallbacks) {
                    cb(elem.id, at);
                  }
                },
                screenToWorld,
                radius,
                className: elem.style?.className || '',
              });

              const dragCircle = dragHandle.element;
              dragCircle.setAttribute('data-figure-id', elem.id);

              nodeMap.set(elem.id, { element: dragCircle, dragHandle, kind: 'point' });
            } else {
              // Update existing drag point
              entry.dragHandle.update(elem.at);
            }
          } else {
            // Use a regular (non-draggable) point circle
            if (entry?.dragHandle) {
              // Was draggable, now isn't - tear down drag handle
              entry.dragHandle.destroy();
              const el = entry.element;
              if (el.parentNode) {
                el.parentNode.removeChild(el);
              }
              nodeMap.delete(elem.id);
            }

            const circle = getOrCreateNode(elem.id, 'point', pointLayer) as SVGCircleElement;
            circle.setAttribute('cx', String(elem.at.x));
            circle.setAttribute('cy', String(elem.at.y));
            circle.setAttribute('r', String(radius));

            if (elem.style?.className) {
              circle.setAttribute('class', elem.style.className);
            }
          }

          break;
        }

        case 'segment': {
          const fromPoint = resolvePoint(elem.from, pointMap);
          const toPoint = resolvePoint(elem.to, pointMap);

          if (fromPoint && toPoint) {
            const line = getOrCreateNode(elem.id, 'segment', lineSegmentLayer) as SVGLineElement;

            line.setAttribute('x1', String(fromPoint.x));
            line.setAttribute('y1', String(fromPoint.y));
            line.setAttribute('x2', String(toPoint.x));
            line.setAttribute('y2', String(toPoint.y));

            if (elem.style?.className) {
              line.setAttribute('class', elem.style.className);
            }
            if (elem.style?.dashed) {
              line.setAttribute('stroke-dasharray', DASH_ARRAY);
            }
          }

          break;
        }

        case 'line': {
          const p1 = resolvePoint(elem.through[0], pointMap);
          const p2 = resolvePoint(elem.through[1], pointMap);

          if (p1 && p2) {
            const [clipStart, clipEnd] = clipLineToRect(p1, p2, rect);

            const line = getOrCreateNode(elem.id, 'line', lineSegmentLayer) as SVGLineElement;

            line.setAttribute('x1', String(clipStart.x));
            line.setAttribute('y1', String(clipStart.y));
            line.setAttribute('x2', String(clipEnd.x));
            line.setAttribute('y2', String(clipEnd.y));

            if (elem.style?.className) {
              line.setAttribute('class', elem.style.className);
            }
            if (elem.style?.dashed) {
              line.setAttribute('stroke-dasharray', DASH_ARRAY);
            }
          }

          break;
        }

        case 'ray': {
          const p1 = resolvePoint(elem.through[0], pointMap);
          const p2 = resolvePoint(elem.through[1], pointMap);

          if (p1 && p2) {
            const [rayStart, rayEnd] = clipRayToRect(p1, p2, rect);

            const line = getOrCreateNode(elem.id, 'ray', lineSegmentLayer) as SVGLineElement;

            line.setAttribute('x1', String(rayStart.x));
            line.setAttribute('y1', String(rayStart.y));
            line.setAttribute('x2', String(rayEnd.x));
            line.setAttribute('y2', String(rayEnd.y));

            if (elem.style?.className) {
              line.setAttribute('class', elem.style.className);
            }
            if (elem.style?.dashed) {
              line.setAttribute('stroke-dasharray', DASH_ARRAY);
            }
          }

          break;
        }

        case 'polygon': {
          const resolvedVertices: Vec2[] = [];
          for (const vertexId of elem.vertices) {
            const point = resolvePoint(vertexId, pointMap);
            if (point) {
              resolvedVertices.push(point);
            }
          }

          if (resolvedVertices.length === elem.vertices.length) {
            const poly = getOrCreateNode(elem.id, 'polygon', polygonLayer) as SVGPolygonElement;

            const pointsStr = resolvedVertices.map((p) => `${p.x},${p.y}`).join(' ');
            poly.setAttribute('points', pointsStr);

            if (elem.style?.className) {
              poly.setAttribute('class', elem.style.className);
            }
          }

          break;
        }

        case 'circle': {
          const centerPoint = resolvePoint(elem.center, pointMap);
          const throughPoint = resolvePoint(elem.through, pointMap);

          if (centerPoint && throughPoint) {
            const radius = dist(centerPoint, throughPoint);

            const circle = getOrCreateNode(elem.id, 'circle', circleArcLayer) as SVGCircleElement;

            circle.setAttribute('cx', String(centerPoint.x));
            circle.setAttribute('cy', String(centerPoint.y));
            circle.setAttribute('r', String(radius));

            if (elem.style?.className) {
              circle.setAttribute('class', elem.style.className);
            }
          }

          break;
        }

        case 'arc': {
          const centerPoint = resolvePoint(elem.center, pointMap);
          const fromPoint = resolvePoint(elem.from, pointMap);
          const toPoint = resolvePoint(elem.to, pointMap);

          if (centerPoint && fromPoint && toPoint) {
            const path = getOrCreateNode(elem.id, 'arc', circleArcLayer) as SVGPathElement;

            const d = getArcPath(centerPoint, fromPoint, toPoint);
            path.setAttribute('d', d);

            if (elem.style?.className) {
              path.setAttribute('class', elem.style.className);
            }
          }

          break;
        }

        case 'label': {
          const anchorPoint = resolvePoint(elem.anchor, pointMap);

          if (anchorPoint) {
            const pos = add(anchorPoint, elem.offset);

            const text = getOrCreateNode(elem.id, 'label', labelLayer) as SVGTextElement;

            // SVG <text> defaults to a black fill and does NOT inherit the CSS
            // `color` property, so without this labels are invisible in dark
            // mode. currentColor resolves to the stage's var(--ink).
            text.setAttribute('fill', 'currentColor');
            text.setAttribute('class', 'geom-figure-label');
            text.setAttribute('x', String(pos.x));
            text.setAttribute('y', String(pos.y));
            // Centre the label on its offset position when requested, so a
            // radial outward offset seats the glyph fully outside a circle
            // (rather than the default left-baseline anchor).
            if (elem.textAnchor) {
              text.setAttribute('text-anchor', elem.textAnchor);
              text.setAttribute('dominant-baseline', 'middle');
            }
            text.textContent = elem.text;
          }

          break;
        }
      }
    }
  }

  const handle: FigureHandle = {
    update(elements: readonly FigureElement[]): void {
      updateScene(elements);
    },

    onDrag(cb: (pointId: string, at: Vec2) => void): void {
      dragCallbacks.push(cb);
    },

    destroy(): void {
      // Clean up all drag handles
      for (const entry of nodeMap.values()) {
        if (entry.dragHandle) {
          entry.dragHandle.destroy();
        }
      }

      nodeMap.clear();

      // Clear all registered drag callbacks
      dragCallbacks.length = 0;

      // Remove all layers
      for (const layer of [
        polygonLayer,
        circleArcLayer,
        lineSegmentLayer,
        pointLayer,
        labelLayer,
      ]) {
        if (layer.parentNode) {
          layer.parentNode.removeChild(layer);
        }
      }
    },
  };

  return handle;
}
